import React, { useEffect, useMemo, useState } from 'react';
import { X, Save } from 'lucide-react';
import { PILLARS } from '../data/content';

const TIERS = ['gold', 'silver', 'bronze'];
const OTHER = '__other__';
const EMPTY = { pillar: PILLARS[0]?.name || '', category: '', tier: 'gold', awarded_to: '', entry_title: '', organisation: '', agency: '', citation: '', nomination_id: '', entry_no: '', published: false };

const inputCls = 'w-full bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm';
const labelCls = 'block text-[10px] tracking-[0.28em] uppercase text-[var(--ink-muted)] mb-1.5';

export const WinnerFormModal = ({ initial, options, onClose, onSave, saving }) => {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(initial || {}) }));
  const [query, setQuery] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pillar = PILLARS.find((p) => p.name === form.pillar);
  const pillarCats = (pillar?.categories || []).map((c) => c.name);
  const isOther = form.category && !pillarCats.includes(form.category);
  const [otherMode, setOtherMode] = useState(!!isOther);
  useEffect(() => { if (isOther) setOtherMode(true); }, [isOther]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return options.filter((o) => `${o.entry_no} ${o.entry_title} ${o.organisation} ${o.category}`.toLowerCase().includes(q)).slice(0, 8);
  }, [query, options]);

  const pick = (o) => {
    setForm((f) => ({ ...f, nomination_id: o.id, entry_no: o.entry_no ?? '', entry_title: o.entry_title || f.entry_title, awarded_to: f.awarded_to || o.organisation || '', organisation: o.organisation || f.organisation, agency: o.agency || f.agency }));
    setQuery('');
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.pillar || !form.category.trim() || !form.awarded_to.trim()) return;
    onSave({ ...form, category: form.category.trim(), entry_no: form.entry_no === '' ? null : Number(form.entry_no), nomination_id: form.nomination_id || null });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="winner-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white border border-[var(--gold-faint)] w-full max-w-2xl p-7 md:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">{initial?.id ? 'Edit Winner' : 'Add Winner'}</div>
            <h3 className="font-serif text-xl text-[var(--ink)] mt-1">Honour roll entry</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="winner-modal-close" className="text-[var(--ink-muted)] hover:text-[var(--ink)]"><X size={18} /></button>
        </div>

        <div className="mb-5">
          <label className={labelCls}>Pick from submitted entries (optional)</label>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by entry no, title or brand…" data-testid="winner-nomination-search" className={inputCls} />
          {matches.length > 0 && (
            <div className="border border-[var(--gold-faint)] border-t-0 divide-y divide-[var(--gold-faint)]/50 bg-white max-h-56 overflow-y-auto" data-testid="winner-nomination-results">
              {matches.map((o) => (
                <button type="button" key={o.id} onClick={() => pick(o)} data-testid={`winner-nomination-option-${o.entry_no}`} className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-alt)]">
                  <span className="text-[var(--gold)] numeral mr-2">#{o.entry_no}</span>{o.entry_title || 'Untitled'} <span className="text-[var(--ink-muted)]">· {o.organisation} · {o.category}</span>
                </button>
              ))}
            </div>
          )}
          {form.nomination_id && <p className="text-[11px] text-[var(--ink-muted)] mt-1.5">Linked to entry #{form.entry_no}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Pillar</label>
            <select value={form.pillar} onChange={(e) => { set('pillar', e.target.value); set('category', ''); setOtherMode(false); }} data-testid="winner-pillar" className={inputCls}>
              {PILLARS.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Category</label>
            {!otherMode ? (
              <select value={form.category} onChange={(e) => { if (e.target.value === OTHER) { setOtherMode(true); set('category', ''); } else set('category', e.target.value); }} data-testid="winner-category" className={inputCls}>
                <option value="">Select category…</option>
                {pillarCats.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value={OTHER}>Other (type manually)</option>
              </select>
            ) : (
              <input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="Category name" data-testid="winner-category-other" className={inputCls} />
            )}
          </div>
          <div>
            <label className={labelCls}>Award</label>
            <select value={form.tier} onChange={(e) => set('tier', e.target.value)} data-testid="winner-tier" className={inputCls}>
              {TIERS.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Entry No. (optional)</label>
            <input value={form.entry_no} onChange={(e) => set('entry_no', e.target.value.replace(/\D/g, ''))} data-testid="winner-entry-no" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Awarded To *</label>
            <input value={form.awarded_to} onChange={(e) => set('awarded_to', e.target.value)} required placeholder="Company / agency / brand receiving the honour" data-testid="winner-awarded-to" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Entry / Campaign Title (optional)</label>
            <input value={form.entry_title} onChange={(e) => set('entry_title', e.target.value)} data-testid="winner-title" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Brand (if different from Awarded To)</label>
            <input value={form.organisation} onChange={(e) => set('organisation', e.target.value)} data-testid="winner-organisation" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Agency</label>
            <input value={form.agency} onChange={(e) => set('agency', e.target.value)} data-testid="winner-agency" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Citation (optional, one line)</label>
            <input value={form.citation} onChange={(e) => set('citation', e.target.value)} data-testid="winner-citation" className={inputCls} />
          </div>
        </div>

        <label className="mt-5 inline-flex items-center gap-2 text-sm text-[var(--ink)] cursor-pointer">
          <input type="checkbox" checked={!!form.published} onChange={(e) => set('published', e.target.checked)} data-testid="winner-published" />
          Published (visible once winners are announced)
        </label>

        <div className="mt-7 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="border border-[var(--gold-faint)] text-[var(--ink-muted)] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase">Cancel</button>
          <button type="submit" disabled={saving} data-testid="winner-save" className="inline-flex items-center gap-2 bg-[var(--walnut)] text-white px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
            <Save size={13} /> {saving ? 'Saving…' : 'Save Winner'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WinnerFormModal;
